/**
 * 비용 시뮬레이터 결과 보조 안내 문구.
 * 영구지침: docs/develop/domain/cost/비용_시뮬레이터_구현_지침.md §7.6
 *
 * 주 결과 금액(copayAmount·totalEstimate)은 건드리지 않고, 응답의 참고 필드로
 * 짧은 안내만 만든다. 두 필드 모두 구 응답에는 없을 수 있다.
 */
import type { CostSimulationResponse } from '@/types/cost'

export interface CostReferenceNote {
  key: 'understaffed' | 'severeGradeSurcharge'
  text: string
}

/** 응답 금액을 그대로 원 단위로 쓴다 (비용 상세의 본인부담금 표기와 같게) */
function formatWon(amount: number): string {
  return `${Math.round(amount).toLocaleString('ko-KR')}원`
}

type NoteInput = Pick<
  CostSimulationResponse,
  'copayAmount' | 'dailyRate' | 'severeGradeSurcharge' | 'understaffedRate'
>

export function getCostReferenceNotes(result: NoteInput): CostReferenceNote[] {
  const notes: CostReferenceNote[] = []

  const under = result.understaffedRate
  if (under && result.dailyRate > 0) {
    // 5% 를 박아 두지 않고 응답 수가로 계산한다 — 수가가 바뀌어도 맞게
    const lowerPercent = Math.round((1 - under.dailyRate / result.dailyRate) * 100)
    // 면제처럼 본인부담금이 줄지 않으면 안내할 것이 없다
    if (lowerPercent > 0 && under.copayAmount < result.copayAmount) {
      notes.push({
        key: 'understaffed',
        text:
          `요양보호사 배치 기준에 따라 하루 이용료가 약 ${lowerPercent}% 낮은 요양원도 있어요. ` +
          `그런 곳이라면 본인부담금은 한 달 약 ${formatWon(under.copayAmount)}이에요.`,
      })
    }
  }

  const surcharge = result.severeGradeSurcharge ?? 0
  if (surcharge > 0) {
    notes.push({
      key: 'severeGradeSurcharge',
      text:
        '1·2등급 방문요양은 이용 시간에 따라 하루 최대 6,000원이 가산돼요. ' +
        `이 조건에서는 한 달 ${formatWon(surcharge)}인데, 공단이 전액 부담해서 본인부담금에는 들어가지 않아요.`,
    })
  }

  return notes
}
